import type { CSSProperties } from "react";
import { Badge, EmptyState, Kicker, Panel, Status, Table, Title } from "@fwf/fwfa-design-system";

import { applyFilters, toFilterable } from "../filters";
import type { FilterState } from "../filters";
import { formatIsoDate, formatRelative } from "../format";
import type { Actions, AdminData, Toasts } from "../hooks";
import type { AdminGcpUser, AdminUser } from "../types";

export type SectionProps = {
  data: AdminData;
  filters: FilterState;
  actions: Actions;
  toasts: Toasts;
};

// The DS wires its mono face through `td.fwfa-table-mono`, which would drag a
// cell's sub-line into mono along with the id. Reading the token directly keeps
// the identifier mono and leaves the sub-line in body type.
const MONO: CSSProperties = { fontFamily: "var(--fwfa-font-mono)" };

/** Provider keys are wire values; these are the words the console shows for them. */
const PROVIDER_LABELS: Record<NonNullable<AdminGcpUser["activeProvider"]>, string> = {
  sprites: "Sprites",
  gcp_compute: "GCP Compute",
};

/** Sandbox state is live, so running animates and the other two sit still. */
function sandboxStatus(status: AdminUser["sandboxStatus"]) {
  if (status === "running") {
    return (
      <Status tone="success" live>
        Running
      </Status>
    );
  }
  // Info, not warning: a persisted sandbox is resting, and it is the same state
  // the Sprites table shows. Spending yellow on it broke the DS accent count.
  if (status === "persisted") return <Status tone="info">Persisted</Status>;
  return <Status tone="info">None</Status>;
}

export function UsersSection({ data, filters }: SectionProps) {
  const overview = data.overview;
  if (!overview) return null;

  // The GCP overview tracks the same people under its own selected-workspace
  // pointer. Merging on user id gives one row per person, instead of the two
  // separate tables an operator used to read side by side.
  const gcpUsers = new Map<string, AdminGcpUser>(
    (data.gcp?.users ?? []).map((user) => [user.userId, user]),
  );

  // Only free text is meaningful here: a person has no workspace state or health
  // of their own, so those two controls stay with the provider rows.
  const users = applyFilters(
    overview.users,
    { ...filters, state: "all", health: "all" },
    (user) => toFilterable("unknown", "n/a", [user.userId, user.sandboxId]),
  );

  const total = overview.users.length;

  return (
    <section className="fwfa-section">
      <div className="fwfa-section-header">
        <div>
          <Kicker>FreshVibe core</Kicker>
          <Title as="h2" className="fwfa-title--section">
            Users
          </Title>
        </div>
        <p className="fwfa-type-label">
          {users.length === total ? `${total} tracked` : `${users.length} of ${total} tracked`}
          {" · generated "}
          {formatIsoDate(overview.generatedAt)}
        </p>
      </div>

      <Panel>
        {users.length === 0 ? (
          <EmptyState title="No tracked users">
            {filters.query
              ? "Nothing matches that search. Clear it to see every tracked user."
              : "Nobody has opened a workspace yet. Users appear here once core sees them."}
          </EmptyState>
        ) : (
          <Table reflow>
            <thead>
              <tr>
                <th>User ID</th>
                <th>Sandbox</th>
                <th>Sandbox status</th>
                <th>Agent</th>
                <th>Active provider</th>
                <th>Last used</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const gcpUser = gcpUsers.get(user.userId);
                return (
                  <tr key={user.userId}>
                    <td data-label="User ID">
                      <span style={MONO}>{user.userId}</span>
                    </td>
                    <td data-label="Sandbox">
                      <div>
                        <span style={MONO}>{user.sandboxId ?? "—"}</span>
                        {/* `source` says where the record came from: live memory or the
                            persisted store. It only means anything with a sandbox to explain. */}
                        {user.sandboxId ? <p className="fwfa-type-label">{user.source}</p> : null}
                      </div>
                    </td>
                    <td data-label="Sandbox status">{sandboxStatus(user.sandboxStatus)}</td>
                    <td data-label="Agent">
                      {user.hasActiveAgent ? (
                        <Status tone="success" live>
                          Connected
                        </Status>
                      ) : (
                        <Status tone="info">Disconnected</Status>
                      )}
                    </td>
                    <td data-label="Active provider">
                      {gcpUser?.activeProvider ? (
                        <div>
                          <Badge>{PROVIDER_LABELS[gcpUser.activeProvider]}</Badge>
                          {gcpUser.activeWorkspaceId ? (
                            <p className="fwfa-type-label" style={MONO}>
                              {gcpUser.activeWorkspaceId}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td data-label="Last used">
                      <div>
                        <span>{formatRelative(user.lastUsed)}</span>
                        {user.createdAt ? (
                          <p className="fwfa-type-label">created {formatRelative(user.createdAt)}</p>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Panel>
    </section>
  );
}
